import { useMemo, useRef, useState } from "react"
import type { ColumnDef, ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useVirtualizer } from "@tanstack/react-virtual"
import { AlertTriangle, ChevronRight, Package } from "lucide-react"
import type {
  RunFeedPreviewColumn,
  RunFeedPreviewDrop,
  RunFeedPreviewResponse,
  RunFeedPreviewRow,
} from "@nova-ai/shared"

import { cn } from "../../lib/utils"
import { StatusBadge } from "../ui/status-badge"
import { StatusText } from "../ui/status-text"
import { Tooltip } from "../ui/tooltip"
import { NeedsOfferPill } from "./run-offers-needed-badge"

// Humanize a feed-drop reason code for display. The shared table stays
// marketplace-neutral: it never hardcodes per-marketplace reason wording (e.g.
// Decathlon's SIZE_CPN), just turns whatever code the backend sends into a
// readable label. Marketplace-specific phrasing, if ever needed, belongs on the
// backend that knows the context — not here.
function dropReasonLabel(reason: string): string {
  return reason.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
}

/** Empty-feed explainer: when a page builds zero rows but the build dropped
 *  variants, name the gates that removed them so the operator isn't left with
 *  an unexplained "No feed rows". Counts come from the (capped) `dropped`
 *  sample; the headline total is the authoritative `droppedCount`. */
function FeedDropNotice({
  dropped,
  droppedCount,
}: {
  dropped: RunFeedPreviewDrop[]
  droppedCount: number
}) {
  const byReason = new Map<string, number>()
  for (const drop of dropped) {
    byReason.set(drop.reason, (byReason.get(drop.reason) ?? 0) + 1)
  }
  const reasons = [...byReason.entries()].sort((a, b) => b[1] - a[1])
  return (
    <div className="flex flex-col gap-2 p-4 text-sm text-muted-foreground">
      <div className="flex items-center gap-2 font-medium text-foreground">
        <AlertTriangle className="h-4 w-4 text-warning" />

        {`${droppedCount.toLocaleString("en-US")} variant ${
          droppedCount === 1 ? "row was" : "rows were"
        } dropped from the feed — nothing to preview.`}
      </div>

      <ul className="flex list-disc flex-col gap-1 pl-5 text-xs">
        {reasons.map(([reason, count]) => (
          <li key={reason}>{`${dropReasonLabel(reason)} — ${count.toLocaleString("en-US")}`}</li>
        ))}
      </ul>

      <p className="text-xs">
        Products with no surviving variants are NOK. Partial families can retain an OK product
        verdict.
      </p>
    </div>
  )
}

/** Empty-state for the feed table: explains the drop when the build removed
 *  rows, else the plain "nothing here". Kept as its own component so the table
 *  body's complexity budget isn't spent on the branch. */
function EmptyFeedPreview({ preview }: { preview: RunFeedPreviewResponse | undefined }) {
  if (preview && preview.droppedCount > 0) {
    return <FeedDropNotice dropped={preview.dropped} droppedCount={preview.droppedCount} />
  }

  return <StatusText>No feed rows available.</StatusText>
}

/** Header chip: count of variant rows the build dropped from this page's feed.
 *  Renders nothing when none were dropped. */
function FeedDroppedBadge({ droppedCount }: { droppedCount: number }) {
  if (droppedCount === 0) {
    return null
  }

  return (
    <Tooltip content="Variant rows the build removed from the feed (e.g. no nutrition-table image). Products with no surviving rows are NOK; partial families can remain OK.">
      <span className="inline-flex items-center gap-1 text-warning">
        <AlertTriangle className="h-3.5 w-3.5" />

        {`${droppedCount.toLocaleString("en-US")} dropped`}
      </span>
    </Tooltip>
  )
}

/** Header chip: of the variants on this page, how many have no marketplace
 *  offer yet (what a new-only build pushes). Renders nothing when the offer
 *  snapshot wasn't read (null) or none need an offer. */
function OffersNeededHeaderBadge({ count }: { count: number | null }) {
  if (count === null || count === 0) {
    return null
  }

  return (
    <Tooltip content="Variants eligible to list with no marketplace offer yet. A new-only offer build would push exactly these.">
      <span className="inline-flex items-center gap-1 text-warning">
        <Package className="h-3.5 w-3.5" />

        {`${count.toLocaleString("en-US")} need offer`}
      </span>
    </Tooltip>
  )
}

export type FeedRowKey = {
  configSku: string
  simpleSku: string
}

/** A config groups its near-identical simples. Parent (config) rows carry the
 *  values shared by every simple plus the set of columns that differ; simple
 *  rows carry their own values. The two row kinds share one shape so a single
 *  accessor (`row.values[code]`) feeds both sorting and rendering. */
type FeedTableRow = {
  kind: "config" | "simple"
  configSku: string
  simpleSku: string
  values: Record<string, string>
  simpleCount: number
  variedColumns: Set<string>
  // Distinct required-attribute codes missing on this row (config rows aggregate
  // across their simples). Drives the missing-required warning sign + tooltip.
  missingRequired: string[]
  // This variant is eligible to list but has no marketplace offer yet (config
  // rows are true if ANY simple needs one). Drives the inline "needs offer"
  // pill.
  needsOffer: boolean
  subRows?: FeedTableRow[]
}

type RunFeedPreviewTableProps = {
  preview: RunFeedPreviewResponse | undefined
  isLoading: boolean
  error: unknown
  selectedRowKey: FeedRowKey | null
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  onSelectRow: (row: FeedRowKey) => void
}

const SKU_COLUMN_WIDTH = 12
const VARIANT_COLUMN_WIDTH = 12
const FEED_COLUMN_WIDTH = 14

export function RunFeedPreviewTable({
  preview,
  isLoading,
  error,
  selectedRowKey,
  sorting,
  onSortingChange,
  onSelectRow,
}: RunFeedPreviewTableProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({})
  // data/columns MUST be referentially stable across renders: a fresh data
  // array invalidates the core row model, whose autoResetPageIndex queues a
  // pagination-state update, which re-renders and builds a fresh array again —
  // an infinite render loop that pins the tab. (The React Compiler bails out
  // on this component because of react-table's instance mutation, so it does
  // not memoize these for us.)
  const data = useMemo(
    () => (preview ? groupRowsByConfig(preview.rows, preview.columns) : []),
    [preview]
  )
  const columns = useMemo(
    () => (preview ? buildColumns(preview.columns, onSelectRow) : []),
    [preview, onSelectRow]
  )
  const table = useReactTable({
    data,
    columns,
    state: { sorting, expanded },
    onSortingChange,
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // This table never paginates; the auto-reset only exists to restart the
    // loop above when data legitimately changes. Keep it off.
    autoResetPageIndex: false,
  })
  const rows = table.getRowModel().rows
  const scrollRef = useRef<HTMLDivElement>(null)
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 38,
    overscan: 8,
  })
  const virtualRows = rowVirtualizer.getVirtualItems()
  const topPadding = virtualRows[0]?.start ?? 0
  const bottomPadding =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0

  if (isLoading) {
    return <StatusText>loading feed preview...</StatusText>
  }

  if (error) {
    return (
      <StatusText variant="error">Feed preview unavailable: {(error as Error).message}</StatusText>
    )
  }

  if (!preview || preview.rows.length === 0) {
    return <EmptyFeedPreview preview={preview} />
  }

  const allExpanded = table.getIsAllRowsExpanded()

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span>{`${preview.rowCount.toLocaleString()} ${preview.rowCount === 1 ? "row" : "rows"} · ${preview.columnCount.toLocaleString()} attributes · ${preview.format.toUpperCase()}`}</span>

          <FeedDroppedBadge droppedCount={preview.droppedCount} />

          <OffersNeededHeaderBadge count={preview.offersNeededCount} />

          <button
            type="button"
            className="font-medium text-foreground hover:underline"
            onClick={table.getToggleAllRowsExpandedHandler()}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>

        <span>{`${preview.configSkuCount} config SKUs`}</span>
      </div>

      {/* Extra bottom padding keeps the last row scrollable out from under the
          floating load-more pill the run page overlays on this area. */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto pb-14">
        <table className="w-max min-w-full border-separate border-spacing-0 text-xs">
          <thead className="sticky top-0 z-30 bg-muted text-left text-muted-foreground">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="border-b border-r border-border bg-muted px-2 py-2 align-bottom font-medium"
                    style={{ width: `${header.getSize()}rem` }}
                  >
                    {header.isPlaceholder ? null : (
                      <button
                        type="button"
                        className="block w-full text-left"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}

                        <span className="mt-1 block h-3 text-xs leading-3 text-muted-foreground">
                          {sortLabel(header.column.getIsSorted())}
                        </span>
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {topPadding > 0 && (
              <tr>
                <td colSpan={table.getVisibleFlatColumns().length} style={{ height: topPadding }} />
              </tr>
            )}

            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              if (!row) {
                return null
              }
              const isConfig = row.original.kind === "config"
              const isSelected =
                !isConfig &&
                selectedRowKey?.configSku === row.original.configSku &&
                selectedRowKey.simpleSku === row.original.simpleSku

              return (
                <tr
                  key={row.id}
                  className={cn(
                    "hover:bg-muted/60",
                    isConfig && "bg-muted/40 hover:bg-muted/60",
                    isSelected && "bg-muted hover:bg-muted"
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        "h-9 border-b border-r border-border px-2 py-1 align-middle",
                        isConfig ? "bg-muted/40" : "bg-card",
                        isSelected && "bg-muted"
                      )}
                      style={{ width: `${cell.column.getSize()}rem` }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              )
            })}

            {bottomPadding > 0 && (
              <tr>
                <td
                  colSpan={table.getVisibleFlatColumns().length}
                  style={{ height: bottomPadding }}
                />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Collapse the flat per-simple feed into one parent row per config. The parent
 *  keeps each column's value only when every simple agrees; columns that differ
 *  (e.g. size) are flagged in `variedColumns` and rendered as `varies`. */
function groupRowsByConfig(
  rows: readonly RunFeedPreviewRow[],
  columns: readonly RunFeedPreviewColumn[]
): FeedTableRow[] {
  const groups = new Map<string, RunFeedPreviewRow[]>()
  for (const row of rows) {
    const existing = groups.get(row.configSku)
    if (existing) {
      existing.push(row)
    } else {
      groups.set(row.configSku, [row])
    }
  }

  const result: FeedTableRow[] = []
  for (const [configSku, simples] of groups) {
    const sharedValues: Record<string, string> = {}
    const variedColumns = new Set<string>()
    for (const column of columns) {
      const first = simples[0]?.values[column.code] ?? ""
      const allSame = simples.every((simple) => (simple.values[column.code] ?? "") === first)
      if (allSame) {
        sharedValues[column.code] = first
      } else {
        variedColumns.add(column.code)
      }
    }

    // Each row carries its own missing-required codes (uncapped, from the API).
    // The config row aggregates the distinct set across its simples.
    const configMissing = [...new Set(simples.flatMap((simple) => simple.missingRequired))]
    // A config needs an offer if ANY of its simples does.
    const configNeedsOffer = simples.some((simple) => simple.needsOffer)

    result.push({
      kind: "config",
      configSku,
      simpleSku: "",
      values: sharedValues,
      simpleCount: simples.filter((simple) => !simple.isPlaceholder).length,
      variedColumns,
      missingRequired: configMissing,
      needsOffer: configNeedsOffer,
      subRows: simples.map((simple) => ({
        kind: "simple",
        configSku: simple.configSku,
        simpleSku: simple.simpleSku,
        values: simple.values,
        simpleCount: 0,
        variedColumns: new Set<string>(),
        missingRequired: simple.missingRequired,
        needsOffer: simple.needsOffer,
      })),
    })
  }
  return result
}

function buildColumns(
  feedColumns: readonly RunFeedPreviewColumn[],
  onSelectRow: (row: FeedRowKey) => void
): ColumnDef<FeedTableRow>[] {
  return [
    {
      id: "configSku",
      header: () => <HeaderLabel label="Config SKU" code="" />,
      accessorKey: "configSku",
      size: SKU_COLUMN_WIDTH,
      cell: ({ row }) =>
        row.original.kind === "config" ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="-ml-1 flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={row.getToggleExpandedHandler()}
              aria-label={row.getIsExpanded() ? "Collapse config" : "Expand config"}
              aria-expanded={row.getIsExpanded()}
            >
              <ChevronRight
                className={cn("size-4 transition-transform", row.getIsExpanded() && "rotate-90")}
              />
            </button>

            <button
              type="button"
              className="whitespace-nowrap font-mono text-foreground hover:underline"
              onClick={() =>
                onSelectRow({
                  configSku: row.original.configSku,
                  simpleSku: row.original.subRows?.[0]?.simpleSku ?? "",
                })
              }
            >
              {row.original.configSku}
            </button>

            <MissingRequiredBadge codes={row.original.missingRequired} />

            <NeedsOfferBadge needsOffer={row.original.needsOffer} />
          </div>
        ) : null,
    },
    {
      id: "simpleSku",
      header: () => <HeaderLabel label="Simple SKU" code="" />,
      accessorKey: "simpleSku",
      size: VARIANT_COLUMN_WIDTH,
      cell: ({ row }) =>
        row.original.kind === "config" ? (
          <button
            type="button"
            className="whitespace-nowrap text-muted-foreground hover:text-foreground hover:underline"
            onClick={row.getToggleExpandedHandler()}
          >
            {row.original.simpleCount === 0
              ? "No feed rows"
              : `${row.original.simpleCount} simple${row.original.simpleCount === 1 ? "" : "s"}`}
          </button>
        ) : (
          <div className="flex items-center gap-2 pl-5">
            <SkuButton
              label={row.original.simpleSku}
              onClick={() =>
                onSelectRow({
                  configSku: row.original.configSku,
                  simpleSku: row.original.simpleSku,
                })
              }
            />

            <MissingRequiredBadge codes={row.original.missingRequired} />

            <NeedsOfferBadge needsOffer={row.original.needsOffer} />
          </div>
        ),
    },
    ...feedColumns.map(
      (column): ColumnDef<FeedTableRow> => ({
        id: column.code,
        header: () => <HeaderLabel label={column.label} code={column.code} />,
        accessorFn: (row) => row.values[column.code] ?? "",
        size: FEED_COLUMN_WIDTH,
        cell: ({ row, getValue }) => <AttributeCell row={row} column={column} value={getValue()} />,
      })
    ),
  ]
}

function AttributeCell({
  row,
  column,
  value,
}: {
  row: Row<FeedTableRow>
  column: RunFeedPreviewColumn
  value: unknown
}) {
  const raw = String(value ?? "")

  if (row.original.kind === "config") {
    if (row.original.variedColumns.has(column.code)) {
      return <span className="italic text-muted-foreground/70">varies</span>
    }
    return <CellValue value={raw} label={column.valueLabels?.[raw]} />
  }

  // A simple's value is dimmed when it matches the config's shared value, so the
  // columns that actually differ between variants pop out when expanded.
  const matchesParent = !row.getParentRow()?.original.variedColumns.has(column.code)
  return <CellValue value={raw} label={column.valueLabels?.[raw]} muted={matchesParent} />
}

/** Missing-required warning sign — same AlertTriangle + count pill the orders
 *  grid uses for problem orders, so "this row needs attention" reads the same
 *  everywhere. Hovering lists exactly which required attributes are missing. */
function MissingRequiredBadge({ codes }: { codes: string[] }) {
  if (codes.length === 0) {
    return null
  }
  return (
    <Tooltip content={<MissingRequiredTooltip codes={codes} />}>
      <StatusBadge tone="destructive" size="sm">
        <AlertTriangle className="size-3" />

        {codes.length}
      </StatusBadge>
    </Tooltip>
  )
}

/** Inline pill on a row that's eligible to list but has no marketplace offer
 *  yet — a new-only offer build would push it. Warning-toned (actionable), not
 *  destructive (a build blocker). */
function NeedsOfferBadge({ needsOffer }: { needsOffer: boolean }) {
  if (!needsOffer) {
    return null
  }
  return (
    <Tooltip content="No marketplace offer yet — a new-only offer build would push this variant.">
      <NeedsOfferPill />
    </Tooltip>
  )
}

function MissingRequiredTooltip({ codes }: { codes: string[] }) {
  return (
    <div>
      <div className="mb-1 font-medium text-destructive">
        {`${codes.length} required attribute${codes.length === 1 ? "" : "s"} missing`}
      </div>

      <ul className="space-y-0.5 font-mono text-xs text-muted-foreground">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
    </div>
  )
}

function HeaderLabel({ label, code }: { label: string; code: string }) {
  return (
    <>
      <span className="block whitespace-normal break-words text-foreground">{label}</span>

      {code ? (
        <span className="mt-1 block whitespace-normal break-words font-mono text-xs font-normal text-muted-foreground">
          {code}
        </span>
      ) : null}
    </>
  )
}

function SkuButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="whitespace-nowrap font-mono text-foreground hover:underline"
      onClick={onClick}
    >
      {label}
    </button>
  )
}

function CellValue({
  value,
  label,
  muted,
}: {
  value: string
  label?: string | undefined
  muted?: boolean
}) {
  // Show the human label when a code resolved; the raw code stays visible in
  // the row drawer (label + code) for verification.
  return (
    <div
      className={cn(
        "min-w-40 max-w-56 overflow-hidden text-ellipsis whitespace-nowrap leading-6",
        muted ? "text-muted-foreground" : "text-foreground"
      )}
    >
      {(label ?? value) || "NULL"}
    </div>
  )
}

function sortLabel(sortState: false | "asc" | "desc"): string {
  if (sortState === "asc") {
    return "asc"
  }
  if (sortState === "desc") {
    return "desc"
  }
  return ""
}
